/**
 * The TypeScript CONTRACT MIRROR of the frozen Capability Definition read model.
 *
 * WHAT THIS IS
 *   The frontend-side mirror of exactly three frozen types, so the consumer layer
 *   can hold a read result without re-modelling it (FE1-W0 Authorization Record
 *   §3.6; FE-1 Stage Work Item §4.1 F).
 *
 * WHAT THIS IS NOT
 *   It is NOT a backend entity, NOT a frontend domain projection, and NOT a
 *   place to add business interpretation. FE-1 Stage Work Item §7.1 (Contract
 *   First): "The frontend consumes the contract. The frontend MUST NOT infer
 *   backend models." Where this mirror and the frozen contract disagree, the
 *   CONTRACT WINS and this file is the defect.
 *
 * MIRRORED FROM, EXACTLY — nothing else is mirrored
 *   backend/contracts/src/main/java/org/rescue/contract/api/readmodel/
 *     CapabilityDefinitionQueryV1.java        blob 31991a72314731cb349ed3e91d849f52b1b8b6c1
 *     CapabilityDefinitionSnapshotV1.java     blob b18230ce4e584d22f20e9fb71683853e626fe6c4
 *     CapabilityDefinitionReadResultV1.java   blob 9ebdb136dca12c207d127a006951dc4eca82e5cf
 *
 * THE AUTHORITY VALUE IS NOT REDECLARED HERE.
 *   `CapabilityDefinitionReadResultV1.Authority { NON_AUTHORITY }` is already
 *   mirrored, from the same blob, by `frontend/src/shared/semantic/authority.ts`
 *   (FE-1 Stage Work Item §4.1 E). Re-declaring it would create a SECOND
 *   authority semantics, which Phase B is forbidden to do. This mirror therefore
 *   CONSUMES the Phase A value and adds no second one.
 *
 * WHY THE RESULT IS A THREE-VARIANT UNION AND NOT ONE FLAT INTERFACE
 *   The frozen record is flat, but its compact constructor enforces two
 *   invariants that a flat interface cannot express:
 *
 *     "AVAILABLE requires a matching definition snapshot"
 *     "absent or unavailable data must not carry a snapshot"
 *
 *   FE-1 Stage Work Item §7.2 requires the frozen separations to be preserved
 *   STRUCTURALLY — "the type system must make violation unrepresentable". So the
 *   mirror makes them unrepresentable: `snapshot` is a required
 *   `CapabilityDefinitionSnapshotV1` on the AVAILABLE variant and `null` on the
 *   other two, and each of the three frozen `Status` members gets its OWN variant
 *   so UNKNOWN and UNAVAILABLE stay distinct types rather than collapsing into one
 *   "not available" bucket.
 *
 * WHY THE RESULT IS BRANDED
 *   The Java compact constructor is the single place the invariants are enforced.
 *   Branding makes the constructors below the single construction site on the
 *   frontend side too: a caller cannot assemble a result literal that skips the
 *   id-equality check, because a bare object is not assignable to the branded
 *   type. The brand is a type-system-only construct with no runtime cost.
 */

import { NON_AUTHORITY, type NonAuthority } from '../../../../shared/semantic/index';

/* ------------------------------------------------------------------------- *
 * 1. CapabilityDefinitionQueryV1
 *    Frozen: record CapabilityDefinitionQueryV1(String capabilityId)
 *    Compact constructor: capabilityId must not be null or blank.
 * ------------------------------------------------------------------------- */

/**
 * A current-definition query. "Current definition data only; no historical
 * lookup or command authority."
 *
 * The frozen constructor rejects null/blank, so the only construction site is
 * `capabilityDefinitionQuery` below. A bare `{ capabilityId: '' }` is a valid
 * object literal but is not what the contract accepts, so the constructor is the
 * sanctioned entry point rather than the interface itself.
 */
export interface CapabilityDefinitionQueryV1 {
  readonly capabilityId: string;
}

/**
 * The frontend defect raised when a query is built that the frozen contract
 * would reject. This is NOT a read outcome: the frozen read plane reports reader
 * failures as UNAVAILABLE, and it has no vocabulary for a malformed query at all
 * — its own constructor throws.
 */
export class CapabilityDefinitionQueryViolation extends Error {
  public constructor(capabilityId: string) {
    super(
      `CapabilityDefinitionQueryV1 requires a non-null, non-blank capabilityId; ` +
        `received ${JSON.stringify(capabilityId)}. This is a frontend defect, not a ` +
        `read-model state (frozen compact constructor: "capabilityId must not be null or blank").`,
    );
    this.name = 'CapabilityDefinitionQueryViolation';
  }
}

/** The only sanctioned construction site for a `CapabilityDefinitionQueryV1`. */
export function capabilityDefinitionQuery(capabilityId: string): CapabilityDefinitionQueryV1 {
  if (capabilityId.trim() === '') {
    throw new CapabilityDefinitionQueryViolation(capabilityId);
  }
  return { capabilityId };
}

/* ------------------------------------------------------------------------- *
 * 2. CapabilityDefinitionSnapshotV1
 *    Frozen: record with EXACTLY eight fields.
 *    "Definition lifecycle is not operational availability, readiness,
 *     commitment, assignment or authorization."
 * ------------------------------------------------------------------------- */

/**
 * `CapabilityDefinitionSnapshotV1.DefinitionStatus` — VERBATIM.
 * "Definition lifecycle only; never an allowed-action vocabulary."
 *
 * There is no fourth member, and none may be added. This vocabulary is NOT a
 * readiness, eligibility or execution-permission vocabulary: `PUBLISHED` does not
 * mean ready, and `DEACTIVATED` does not mean forbidden (FE-1 Stage Work Item §6.4).
 */
export type DefinitionStatus = 'DRAFT' | 'PUBLISHED' | 'DEACTIVATED';

/** The closed lifecycle vocabulary. The only construction site for the members. */
export const DefinitionStatus = Object.freeze({
  DRAFT: 'DRAFT' as DefinitionStatus,
  PUBLISHED: 'PUBLISHED' as DefinitionStatus,
  DEACTIVATED: 'DEACTIVATED' as DefinitionStatus,
});

/**
 * Immutable definition values — the eight adopted fields, and no ninth.
 *
 * `definitionVersion` and `aggregateVersion` mirror the frozen `long`. See the
 * recorded finding `FE1-W0B-F-04`: `number` is the JS mirror of `long` and is
 * exact only below 2^53, which the frozen `>= 1` version counters do not reach.
 */
export interface CapabilityDefinitionSnapshotV1 {
  readonly capabilityId: string;
  readonly displayName: string;
  readonly capabilityType: string;
  readonly definitionVersion: number;
  readonly definitionDigest: string;
  readonly definitionStatus: DefinitionStatus;
  readonly aggregateVersion: number;
  readonly originLabel: string;
}

/* ------------------------------------------------------------------------- *
 * 3. CapabilityDefinitionReadResultV1
 *    Frozen: record(Status status, String capabilityId, Snapshot snapshot)
 *    "Fixed invariant; callers cannot supply or elevate authority."
 * ------------------------------------------------------------------------- */

/**
 * `CapabilityDefinitionReadResultV1.Status` — VERBATIM.
 * "AVAILABLE means only that snapshot data was retrieved, never that the
 * capability is operationally available or ready. UNKNOWN is normal data
 * absence, not an error. UNAVAILABLE means a functioning reader could not
 * supply a trustworthy response."
 */
export type CapabilityDefinitionReadStatus = 'AVAILABLE' | 'UNKNOWN' | 'UNAVAILABLE';

/** The closed read-status vocabulary. The only construction site for the members. */
export const CapabilityDefinitionReadStatus = Object.freeze({
  AVAILABLE: 'AVAILABLE' as CapabilityDefinitionReadStatus,
  UNKNOWN: 'UNKNOWN' as CapabilityDefinitionReadStatus,
  UNAVAILABLE: 'UNAVAILABLE' as CapabilityDefinitionReadStatus,
});

/**
 * The brand that makes the constructors below the only construction site.
 *
 * A PHANTOM brand, in exactly the form `frontend/src/shared/semantic/brand.ts` uses: it
 * is introduced with `declare const` and therefore has NO runtime representation
 * and NO runtime cost.
 *
 * IT MUST NOT BE A REAL PROPERTY KEY. A property keyed by a type-only symbol does
 * not exist at runtime, so a constructor that tried to set one would throw a
 * `ReferenceError` on its first call — which is precisely what an earlier revision
 * of this file did, and precisely why this note is here. The brand is applied
 * through the `ReadResultBranded` intersection below, which is erased at build.
 */
declare const readResultBrand: unique symbol;

/** Applies the phantom brand to a variant shape. Erased at build; no runtime cost. */
type ReadResultBranded<TShape> = TShape & {
  readonly [readResultBrand]: 'CapabilityDefinitionReadResultV1';
};

/** The fields every variant carries, in the frozen record's own order. */
interface CapabilityDefinitionReadResultCommon {
  readonly capabilityId: string;
  /** Fixed invariant; callers cannot supply or elevate authority. */
  readonly authority: NonAuthority;
}

/** Snapshot data was retrieved. NOT a claim of operational availability. */
export type CapabilityDefinitionAvailableResultV1 = ReadResultBranded<
  CapabilityDefinitionReadResultCommon & {
    readonly status: 'AVAILABLE';
    /** REQUIRED here and `null` on the other two: see the header. */
    readonly snapshot: CapabilityDefinitionSnapshotV1;
  }
>;

/** Normal data absence. NOT an error, NOT empty, NOT a default. */
export type CapabilityDefinitionUnknownResultV1 = ReadResultBranded<
  CapabilityDefinitionReadResultCommon & {
    readonly status: 'UNKNOWN';
    readonly snapshot: null;
  }
>;

/** A functioning reader could not supply a trustworthy response. No diagnostic. */
export type CapabilityDefinitionUnavailableResultV1 = ReadResultBranded<
  CapabilityDefinitionReadResultCommon & {
    readonly status: 'UNAVAILABLE';
    readonly snapshot: null;
  }
>;

/** Everything the read plane may return for a capability definition query. */
export type CapabilityDefinitionReadResultV1 =
  | CapabilityDefinitionAvailableResultV1
  | CapabilityDefinitionUnknownResultV1
  | CapabilityDefinitionUnavailableResultV1;

/**
 * The frontend defect raised when a snapshot would violate the frozen compact
 * constructor. Like the query violation above, this is NOT a read outcome.
 */
export class CapabilityDefinitionSnapshotViolation extends Error {
  public constructor(reason: string) {
    super(
      `CapabilityDefinitionSnapshotV1 invariant violated: ${reason}. ` +
        `This is a frontend defect, not a read-model state.`,
    );
    this.name = 'CapabilityDefinitionSnapshotViolation';
  }
}

/**
 * The only sanctioned construction site for a `CapabilityDefinitionSnapshotV1`.
 *
 * Enforces the frozen compact constructor's checks that TypeScript cannot express
 * structurally: the text fields must be non-blank and both versions must be >= 1.
 * Non-nullability is already structural (`strict` + `readonly x: string`).
 */
export function capabilityDefinitionSnapshot(
  fields: CapabilityDefinitionSnapshotV1,
): CapabilityDefinitionSnapshotV1 {
  for (const [name, text] of [
    ['capabilityId', fields.capabilityId],
    ['displayName', fields.displayName],
    ['capabilityType', fields.capabilityType],
    ['definitionDigest', fields.definitionDigest],
    ['originLabel', fields.originLabel],
  ] as const) {
    if (text.trim() === '') {
      throw new CapabilityDefinitionSnapshotViolation(`${name} must not be blank`);
    }
  }
  if (fields.definitionVersion < 1 || fields.aggregateVersion < 1) {
    throw new CapabilityDefinitionSnapshotViolation(
      'definition and aggregate versions must be >= 1',
    );
  }
  return fields;
}

/**
 * The only sanctioned construction site for an AVAILABLE result.
 *
 * Enforces "AVAILABLE requires a matching definition snapshot" — the check the
 * frozen compact constructor performs and the one a flat interface cannot carry.
 */
export function availableResult(
  capabilityId: string,
  snapshot: CapabilityDefinitionSnapshotV1,
): CapabilityDefinitionAvailableResultV1 {
  if (capabilityId !== snapshot.capabilityId) {
    throw new CapabilityDefinitionSnapshotViolation(
      `AVAILABLE requires a matching definition snapshot: query id ` +
        `${JSON.stringify(capabilityId)} does not match snapshot id ` +
        `${JSON.stringify(snapshot.capabilityId)}`,
    );
  }
  return {
    status: 'AVAILABLE',
    capabilityId,
    snapshot,
    authority: NON_AUTHORITY,
  } as CapabilityDefinitionAvailableResultV1;
}

/** The only sanctioned construction site for an UNKNOWN result. Carries no snapshot. */
export function unknownResult(capabilityId: string): CapabilityDefinitionUnknownResultV1 {
  return {
    status: 'UNKNOWN',
    capabilityId,
    snapshot: null,
    authority: NON_AUTHORITY,
  } as CapabilityDefinitionUnknownResultV1;
}

/**
 * The only sanctioned construction site for an UNAVAILABLE result.
 * Carries no snapshot and NO diagnostic: the frozen read seam publishes none.
 */
export function unavailableResult(capabilityId: string): CapabilityDefinitionUnavailableResultV1 {
  return {
    status: 'UNAVAILABLE',
    capabilityId,
    snapshot: null,
    authority: NON_AUTHORITY,
  } as CapabilityDefinitionUnavailableResultV1;
}

/* ------------------------------------------------------------------------- *
 * 4. Compile-time assertions of the mirror's shape.
 *
 *    These produce no runtime code. They are part of the typecheck surface, in
 *    the same spirit as `frontend/src/shared/semantic/invariants.ts`: a runtime test
 *    proves a property held on one input, an assertion proves it holds for every
 *    input at every build. They live beside the declarations they assert because
 *    they assert THIS module's shape.
 * ------------------------------------------------------------------------- */

/** Fails to compile unless `T` is exactly `true`. */
type AssertTrue<T extends true> = T;

/** `true` only when the two types share no member. */
type IsDisjoint<TLeft, TRight> = [Extract<TLeft, TRight>] extends [never] ? true : false;

/** `true` only when the two unions have exactly the same members. */
type IsExactly<TLeft, TRight> = [TLeft] extends [TRight]
  ? [TRight] extends [TLeft]
    ? true
    : false
  : false;

/** The eight adopted fields, from FE-1 Stage Work Item §6.2. A ninth is forbidden. */
type AdoptedSnapshotFields =
  | 'capabilityId'
  | 'displayName'
  | 'capabilityType'
  | 'definitionVersion'
  | 'definitionDigest'
  | 'definitionStatus'
  | 'aggregateVersion'
  | 'originLabel';

/** Catches BOTH a missing field and a ninth field added to the snapshot. */
export type SnapshotFieldsAreExactlyTheAdoptedEight = AssertTrue<
  IsExactly<keyof CapabilityDefinitionSnapshotV1, AdoptedSnapshotFields>
>;

/** The read result carries the frozen record's own three fields and no more. */
export type ResultFieldsAreExactlyTheFrozenThree = AssertTrue<
  IsExactly<
    keyof CapabilityDefinitionAvailableResultV1,
    'status' | 'capabilityId' | 'snapshot' | 'authority' | typeof readResultBrand
  >
>;

/** Every vocabulary is closed. Widening one to `string` fails the build. */
export type DefinitionStatusVocabularyIsClosed = AssertTrue<
  string extends DefinitionStatus ? false : true
>;
export type ReadStatusVocabularyIsClosed = AssertTrue<
  string extends CapabilityDefinitionReadStatus ? false : true
>;

/** The three read statuses stay three distinct types, not one bucket. */
export type UnknownIsNotUnavailable = AssertTrue<
  IsDisjoint<CapabilityDefinitionUnknownResultV1, CapabilityDefinitionUnavailableResultV1>
>;
export type AvailableIsNotUnknown = AssertTrue<
  IsDisjoint<CapabilityDefinitionAvailableResultV1, CapabilityDefinitionUnknownResultV1>
>;

/** Definition lifecycle is NOT an allowed-action vocabulary (FE-1 §6.4). */
export type DefinitionStatusHasNoPermissionMember = AssertTrue<
  IsDisjoint<DefinitionStatus, 'ALLOWED' | 'READY' | 'PERMITTED' | 'ENABLED'>
>;

/**
 * `Available != Assigned`, `Definition != Readiness` — asserted at the vocabulary
 * level: none of these over-claiming words exists in any mirrored vocabulary, so
 * no mirror field can carry one.
 */
export type MirrorInventsNoOverClaimingVocabulary = AssertTrue<
  IsDisjoint<
    DefinitionStatus | CapabilityDefinitionReadStatus,
    'READY' | 'ASSIGNED' | 'COMMITTED' | 'OPERATIONAL' | 'AUTHORIZED'
  >
>;

/** Authority has exactly one member and is not a boolean (FE-1 §7.2, §10). */
export type MirrorAuthorityIsNotBoolean = AssertTrue<
  [NonAuthority] extends [boolean] ? false : true
>;

/**
 * The brand is exported as a type so the assertion above can name it, but it is
 * declared locally: `readResultBrand` must not become an importable symbol, or a
 * caller could brand its own literal and bypass the constructors.
 */
export type ReadResultBrand = typeof readResultBrand;
